import { Component, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { SocialIconComponent } from '../../../shared/social-icon.component';
import { I18nService } from '../../../core/i18n.service';
import { BrandShowcaseService, ShowcaseBranch } from './brand-showcase.service';
import { SiteConfigService } from '../../../core/site-config.service';

@Component({
  selector: 'app-branch-showcase',
  imports: [SocialIconComponent, RouterLink],
  template: `
    <div class="branch-grid">
      @for (branch of branches(); track branch.id) {
        <article>
          @if (branch.imageUrl) { <img [src]="branch.imageUrl" [alt]="branch.name" /> }
          <h3>{{ branch.name }}</h3>
          <p>{{ branch.address }}</p>
          @if (branch.workingHours) { <p class="hours">{{ hoursText(branch.workingHours) }}</p> }
          <nav aria-label="Şube iletişim bağlantıları">
            @if (branch.phone) {
              <a class="branch-icon" [href]="'tel:' + branch.phone" aria-label="Şubeyi ara" title="Ara"><app-social-icon platform="phone" /></a>
            }
            @if (branch.mapsLink) {
              <a class="branch-icon" [href]="branch.mapsLink" target="_blank" rel="noopener" aria-label="Yol tarifi al" title="Yol tarifi"><app-social-icon platform="googleMaps" /></a>
            }
            @for (social of branch.socialAccounts || []; track social.id) {
              <a class="branch-icon" [href]="social.url" target="_blank" rel="noopener" [attr.aria-label]="social.label" [title]="social.label" (click)="track(social.id)"><app-social-icon [platform]="social.platform" /></a>
            }
            @if (site.enabled('appointments')) { <a class="appointment-link" [routerLink]="i18n.routePath('appointment')" [queryParams]="{branchID: branch.id}">Randevu al</a> }
          </nav>
        </article>
      }
    </div>
  `,
  styles: [`
    .branch-grid{display:grid;gap:16px;grid-template-columns:repeat(2,minmax(0,1fr))}
    article{border-left:3px solid var(--accent);display:grid;gap:12px;min-width:0;padding:12px 24px}
    img{aspect-ratio:16/8;border-radius:20px;object-fit:cover;width:100%}
    h3{color:var(--ink);font-family:Georgia,serif;font-size:1.55rem;margin:auto 0 0}
    p{color:var(--muted);line-height:1.6;margin:0}.hours{font-size:.9rem}.appointment-link{border:1px solid var(--accent);border-radius:999px;color:var(--accent);padding:9px 14px;text-decoration:none}
    nav{align-items:center;display:flex;flex-wrap:wrap;gap:10px}
    .branch-icon{align-items:center;background:color-mix(in srgb,var(--accent) 10%,var(--surface));border:1px solid color-mix(in srgb,var(--accent) 24%,var(--border));border-radius:50%;color:var(--accent);display:inline-flex;height:42px;justify-content:center;transition:transform .2s ease,background .2s ease;width:42px}
    .branch-icon:hover{background:color-mix(in srgb,var(--accent) 20%,var(--surface));transform:translateY(-2px)}
    @media(max-width:640px){.branch-grid{grid-template-columns:repeat(2,minmax(0,1fr))}article{padding:10px 16px}}
    @media(max-width:460px){.branch-grid{grid-template-columns:1fr}}
  `]
})
export class BranchShowcaseComponent {
  readonly branches = input<ShowcaseBranch[]>([]);
  private readonly showcase = inject(BrandShowcaseService);
  protected readonly site = inject(SiteConfigService);
  protected readonly i18n = inject(I18nService);
  protected track(id: number): void { this.showcase.trackSocial(id); }
  protected hoursText(value: unknown): string {
    const rows = Array.isArray(value) ? value : [];
    const periods = rows
      .filter((item: any) => !item?.isClosed)
      .map((item: any) => ({ day: Number(item?.dayOfWeek), start: this.timeParts(item?.openTime || item?.startTime), end: this.timeParts(item?.closeTime || item?.endTime) }))
      .filter((item) => item.day >= 1 && item.day <= 7 && item.start !== null);
    if (!periods.length) return this.i18n.t('branchHoursUnavailable');

    // Branch hours are stored in UTC. Date converts the next UTC opening into
    // the visitor's local timezone before it is rendered.
    const now = new Date();
    const utcToday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const currentDay = now.getUTCDay() || 7;
    const makeCandidate = (period: (typeof periods)[number], offset: number) => {
      const startAt = new Date(utcToday);
      startAt.setUTCDate(startAt.getUTCDate() + offset);
      startAt.setUTCHours(period.start![0], period.start![1], 0, 0);
      const endAt = new Date(startAt);
      const endParts = period.end ?? [23, 59];
      endAt.setUTCHours(endParts[0], endParts[1], 0, 0);
      return { ...period, startAt, endAt };
    };
    const active = periods
      .filter((period) => period.day === currentDay)
      .map((period) => makeCandidate(period, 0))
      .find((item) => now >= item.startAt && now <= item.endAt);
    if (active) return this.i18n.t('branchOpenNow');

    const candidates = periods.map((period) => {
      const offset = (period.day - currentDay + 7) % 7;
      const today = makeCandidate(period, offset);
      return today.startAt.getTime() > now.getTime() ? today : makeCandidate(period, offset + 7);
    }).sort((a, b) => a.startAt.getTime() - b.startAt.getTime());
    const next = candidates[0];
    const time = this.formatTime(next.startAt);
    const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const nextDate = new Date(next.startAt.getFullYear(), next.startAt.getMonth(), next.startAt.getDate()).getTime();
    const dayDiff = Math.round((nextDate - nowDate) / 86400000);
    let openingText: string;
    if (dayDiff === 0) openingText = this.i18n.t('branchOpensToday').replace(':time', time);
    else if (dayDiff === 1) openingText = this.i18n.t('branchOpensTomorrow').replace(':time', time);
    else openingText = this.i18n.t('branchOpensOn').replace(':day', this.dayLabel(next.startAt)).replace(':time', time);
    return `${this.i18n.t('branchClosedNow')} · ${openingText}`;
  }
  private timeParts(value: unknown): [number, number] | null {
    const match = String(value ?? '').match(/(\d{1,2}):(\d{2})/);
    if (!match) return null;
    return [Math.min(23, Number(match[1])), Math.min(59, Number(match[2]))];
  }
  private formatTime(value: Date): string {
    return new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' }).format(value);
  }
  private dayLabel(value: Date): string {
    return new Intl.DateTimeFormat(undefined, { weekday: 'long' }).format(value);
  }
}
